import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";

const PLANS = [
  {
    id: "basic",
    name: "Basic",
    price: 9.99,
    priceLabel: "$9.99",
    period: "/month",
    tagline: "Great for getting started",
    features: ["Up to 10 job applications/month", "Basic capability matching", "Job board access", "Availability toggle"],
    color: "emerald",
  },
  {
    id: "pro",
    name: "Pro",
    price: 29.99,
    priceLabel: "$29.99",
    period: "/month",
    tagline: "For serious earners",
    badge: "Most Popular",
    features: ["Unlimited job applications", "AI-powered capability matching", "Full marketplace access", "AI resume + cover letter", "Priority job matching", "Profile optimization"],
    color: "emerald",
  },
];

type FormData = {
  firstName: string;
  lastName: string;
  email: string;
  cardNumber: string;
  expiry: string;
  cvc: string;
  nameOnCard: string;
  plan: string;
  billing: "monthly" | "annual";
};

function formatCardNumber(val: string) {
  return val.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();
}
function formatExpiry(val: string) {
  const digits = val.replace(/\D/g, "").slice(0, 4);
  if (digits.length >= 3) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return digits;
}

export default function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const planParam = searchParams.get("plan") ?? "pro";

  const [selectedPlan, setSelectedPlan] = useState(planParam);
  const [billing, setBilling] = useState<"monthly" | "annual">("monthly");
  const [step, setStep] = useState<1 | 2>(1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState<FormData>({
    firstName: "",
    lastName: "",
    email: "",
    cardNumber: "",
    expiry: "",
    cvc: "",
    nameOnCard: "",
    plan: planParam,
    billing: "monthly",
  });

  useEffect(() => {
    setForm((f) => ({ ...f, plan: selectedPlan, billing }));
  }, [selectedPlan, billing]);

  const activePlan = PLANS.find((p) => p.id === selectedPlan) ?? PLANS[1];
  const annualPrice = (activePlan.price * 0.8 * 12).toFixed(2);
  const displayPrice = billing === "annual" ? `$${(activePlan.price * 0.8).toFixed(2)}` : activePlan.priceLabel;
  const totalDue = billing === "annual" ? `$${annualPrice}` : activePlan.priceLabel;

  const set = (k: keyof FormData, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const validateStep1 = () => {
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      setError("Please fill in all account details.");
      return false;
    }
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
    if (!emailOk) { setError("Please enter a valid email address."); return false; }
    setError("");
    return true;
  };

  const validateStep2 = () => {
    const rawCard = form.cardNumber.replace(/\s/g, "");
    if (rawCard.length < 16) { setError("Please enter a valid 16-digit card number."); return false; }
    if (form.expiry.length < 5) { setError("Please enter a valid expiry date."); return false; }
    if (form.cvc.length < 3) { setError("Please enter a valid CVC."); return false; }
    if (!form.nameOnCard.trim()) { setError("Please enter the name on your card."); return false; }
    setError("");
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateStep2()) return;
    setLoading(true);
    setError("");
    const data = new URLSearchParams();
    data.append("firstName", form.firstName);
    data.append("lastName", form.lastName);
    data.append("email", form.email);
    data.append("plan", `${activePlan.name} — ${displayPrice}${activePlan.period}`);
    data.append("billing", billing === "annual" ? "Annual (20% off)" : "Monthly");
    data.append("nameOnCard", form.nameOnCard);
    // Card last 4 only for reference — never send full card over plain form
    const last4 = form.cardNumber.replace(/\s/g, "").slice(-4);
    data.append("cardLast4", `****${last4}`);
    try {
      const res = await fetch("https://readdy.ai/api/form/d76945lr5g02gnd69t00", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok || res.status === 200) {
        setSubmitted(true);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#f3f8f5] flex flex-col">
        <SiteHeader />
        <div className="flex-1 flex items-center justify-center px-4 pt-28 pb-16">
          <div className="bg-white rounded-3xl border border-gray-100 p-12 max-w-md w-full text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-6">
              <i className="ri-check-double-line text-emerald-600 text-4xl" />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900 mb-3">You&apos;re all set!</h2>
            <p className="text-gray-500 mb-2 text-sm leading-relaxed">
              Welcome to Gig <span className="italic">n&apos;</span> Cash <strong>{activePlan.name}</strong>! Check your inbox at <strong>{form.email}</strong> — your account access is on the way.
            </p>
            <div className="bg-emerald-50 rounded-2xl border border-emerald-100 px-5 py-4 mb-8 text-left">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold text-gray-700">Plan</span>
                <span className="text-sm font-bold text-emerald-700">{activePlan.name}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700">Billing</span>
                <span className="text-sm font-bold text-gray-900">{billing === "annual" ? `$${annualPrice}/year` : `${displayPrice}/month`}</span>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => navigate("/dashboard")}
                className="w-full py-3.5 bg-emerald-500 text-white font-bold rounded-xl text-sm hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-dashboard-line mr-2" />Go to Dashboard
              </button>
              <Link to="/find-work" className="w-full py-3 border border-gray-200 text-gray-600 font-semibold rounded-xl text-sm hover:border-emerald-400 hover:text-emerald-600 transition-colors text-center whitespace-nowrap">
                Browse Jobs
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f3f8f5] font-sans">
      <SiteHeader />

      <div className="max-w-6xl mx-auto px-4 md:px-8 pt-28 pb-16">
        {/* Back link */}
        <Link to="/pricing" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors mb-8">
          <i className="ri-arrow-left-line" />Back to Pricing
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left: Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              {/* Step indicator */}
              <div className="flex border-b border-gray-100">
                {[{ n: 1, label: "Account Details" }, { n: 2, label: "Payment" }].map((s) => (
                  <div
                    key={s.n}
                    className={`flex-1 flex items-center gap-2 px-6 py-4 text-sm font-bold transition-colors ${
                      step === s.n ? "text-emerald-700 border-b-2 border-emerald-500" : step > s.n ? "text-gray-400" : "text-gray-300"
                    }`}
                  >
                    <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-extrabold ${
                      step > s.n ? "bg-emerald-500 text-white" : step === s.n ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-400"
                    }`}>
                      {step > s.n ? <i className="ri-check-line" /> : s.n}
                    </span>
                    {s.label}
                  </div>
                ))}
              </div>

              <div className="p-7">
                {/* Step 1: Account Info */}
                {step === 1 && (
                  <div className="flex flex-col gap-5">
                    <div>
                      <h2 className="text-xl font-extrabold text-gray-900 mb-1">Create your account</h2>
                      <p className="text-sm text-gray-500">You&apos;ll use these details to log in to Gig <span className="italic">n&apos;</span> Cash.</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">First Name</label>
                        <input
                          type="text"
                          value={form.firstName}
                          onChange={(e) => set("firstName", e.target.value)}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                          placeholder="First name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">Last Name</label>
                        <input
                          type="text"
                          value={form.lastName}
                          onChange={(e) => set("lastName", e.target.value)}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                          placeholder="Last name"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Email Address</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={(e) => set("email", e.target.value)}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                        placeholder="you@example.com"
                      />
                    </div>

                    {/* Plan selector */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-3">Choose Your Plan</label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {PLANS.map((plan) => (
                          <button
                            key={plan.id}
                            type="button"
                            onClick={() => setSelectedPlan(plan.id)}
                            className={`relative text-left p-4 rounded-xl border-2 transition-all cursor-pointer ${
                              selectedPlan === plan.id
                                ? "border-emerald-500 bg-emerald-50"
                                : "border-gray-200 bg-white hover:border-gray-300"
                            }`}
                          >
                            {plan.badge && (
                              <span className="absolute -top-2.5 right-3 bg-orange-500 text-white text-[10px] font-extrabold px-2.5 py-0.5 rounded-full whitespace-nowrap">
                                {plan.badge}
                              </span>
                            )}
                            <div className={`text-xs font-extrabold uppercase tracking-widest mb-1 ${selectedPlan === plan.id ? "text-emerald-600" : "text-gray-400"}`}>
                              {plan.name}
                            </div>
                            <div className="flex items-baseline gap-1">
                              <span className="text-2xl font-black text-gray-900">{billing === "annual" ? `$${(plan.price * 0.8).toFixed(2)}` : plan.priceLabel}</span>
                              <span className="text-xs text-gray-400">/mo</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">{plan.tagline}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Billing toggle */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-3">Billing Cycle</label>
                      <div className="bg-gray-100 rounded-full p-1 flex gap-1 w-fit">
                        <button
                          type="button"
                          onClick={() => setBilling("monthly")}
                          className={`px-5 py-2 text-xs font-bold rounded-full transition-all cursor-pointer whitespace-nowrap ${billing === "monthly" ? "bg-white text-gray-900" : "text-gray-500"}`}
                        >
                          Monthly
                        </button>
                        <button
                          type="button"
                          onClick={() => setBilling("annual")}
                          className={`px-5 py-2 text-xs font-bold rounded-full transition-all cursor-pointer whitespace-nowrap ${billing === "annual" ? "bg-white text-gray-900" : "text-gray-500"}`}
                        >
                          Annual <span className="text-emerald-600 ml-1">Save 20%</span>
                        </button>
                      </div>
                    </div>

                    {error && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-2">{error}</p>}

                    <button
                      type="button"
                      onClick={() => { if (validateStep1()) setStep(2); }}
                      className="w-full py-3.5 bg-emerald-500 text-white font-bold rounded-xl text-sm hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Continue to Payment <i className="ri-arrow-right-line ml-1" />
                    </button>
                  </div>
                )}

                {/* Step 2: Payment */}
                {step === 2 && (
                  <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">
                    <div>
                      <h2 className="text-xl font-extrabold text-gray-900 mb-1">Payment details</h2>
                      <p className="text-sm text-gray-500">Your card will be charged {billing === "annual" ? `$${annualPrice} today for the year` : `${displayPrice} today, then monthly`}.</p>
                    </div>

                    <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
                        <i className="ri-shield-keyhole-line text-emerald-600" />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-gray-800">Secure 256-bit SSL Payment</div>
                        <div className="text-xs text-gray-500">Your payment info is encrypted and never stored on our servers.</div>
                      </div>
                      <div className="ml-auto flex gap-1.5">
                        {["ri-visa-line", "ri-mastercard-line", "ri-paypal-line"].map((icon) => (
                          <div key={icon} className="w-8 h-5 flex items-center justify-center bg-white border border-gray-200 rounded text-xs">
                            <i className={`${icon} text-gray-600`} />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Name on Card</label>
                      <input
                        type="text"
                        value={form.nameOnCard}
                        onChange={(e) => set("nameOnCard", e.target.value)}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                        placeholder="Full name as on card"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Card Number</label>
                      <div className="relative">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={form.cardNumber}
                          onChange={(e) => set("cardNumber", formatCardNumber(e.target.value))}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-400 pr-12"
                          placeholder="1234 5678 9012 3456"
                          maxLength={19}
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-300">
                          <i className="ri-bank-card-line" />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">Expiry Date</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={form.expiry}
                          onChange={(e) => set("expiry", formatExpiry(e.target.value))}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                          placeholder="MM/YY"
                          maxLength={5}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">CVC</label>
                        <div className="relative">
                          <input
                            type="text"
                            inputMode="numeric"
                            value={form.cvc}
                            onChange={(e) => set("cvc", e.target.value.replace(/\D/g, "").slice(0, 4))}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-400 pr-10"
                            placeholder="CVC"
                            maxLength={4}
                          />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-300">
                            <i className="ri-question-line text-sm" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {error && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-2">{error}</p>}

                    <div className="flex flex-col gap-3 pt-1">
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-4 bg-emerald-500 text-white font-bold rounded-xl text-sm hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                      >
                        {loading ? (
                          <span className="flex items-center justify-center gap-2">
                            <i className="ri-loader-4-line animate-spin" />Processing...
                          </span>
                        ) : (
                          <>
                            <i className="ri-lock-2-line mr-2" />
                            Pay {totalDue} {billing === "annual" ? "/ year" : "/ month"}
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setStep(1); setError(""); }}
                        className="text-sm text-gray-400 hover:text-gray-700 transition-colors cursor-pointer"
                      >
                        <i className="ri-arrow-left-line mr-1" />Back to account details
                      </button>
                    </div>

                    <p className="text-xs text-center text-gray-400">
                      14-day free trial · Cancel anytime · No hidden fees
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>

          {/* Right: Order Summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-gray-100 p-6 sticky top-28">
              <h3 className="text-sm font-extrabold text-gray-700 uppercase tracking-widest mb-5">Order Summary</h3>

              {/* Plan card */}
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 mb-5">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-extrabold text-gray-900">{activePlan.name} Plan</span>
                  {activePlan.badge && (
                    <span className="bg-orange-500 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full whitespace-nowrap">{activePlan.badge}</span>
                  )}
                </div>
                <div className="text-xs text-gray-500 mb-3">{activePlan.tagline}</div>
                <ul className="flex flex-col gap-1.5">
                  {activePlan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-700">
                      <i className="ri-check-line text-emerald-500 flex-shrink-0" />{f}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Price breakdown */}
              <div className="flex flex-col gap-2 mb-5 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>{activePlan.name} ({billing === "annual" ? "Annual" : "Monthly"})</span>
                  <span className="font-semibold">{displayPrice}</span>
                </div>
                {billing === "annual" && (
                  <div className="flex justify-between text-emerald-600 text-xs font-semibold">
                    <span>Annual discount (20% off)</span>
                    <span>-${(activePlan.price * 0.2 * 12).toFixed(2)}/yr</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-600">
                  <span>14-day free trial</span>
                  <span className="text-emerald-600 font-semibold">FREE</span>
                </div>
                <div className="h-px bg-gray-100 my-1" />
                <div className="flex justify-between font-extrabold text-gray-900 text-base">
                  <span>Due today</span>
                  <span>{totalDue}</span>
                </div>
              </div>

              {/* Trust pills */}
              <div className="flex flex-col gap-2">
                {[
                  { icon: "ri-shield-check-line", text: "Secure payment · SSL encrypted" },
                  { icon: "ri-refresh-line", text: "Cancel anytime, no penalties" },
                  { icon: "ri-customer-service-2-line", text: "24/7 support included" },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-2 text-xs text-gray-500">
                    <i className={`${item.icon} text-emerald-500 flex-shrink-0`} />
                    {item.text}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
